// The read-only client page's headline facts, derived from what is already
// stored (steps, Finance Config, Finance Data). Pure functions; nothing here
// writes. Spec: docs/superpowers/specs/2026-10-17-client-page-release-1-design.md
import { calcSessionBlock, calcCumulativeBalance, calcMonth, addMonths } from "../finance/calc";

export const CLIENT_PHASES = ["Activation", "In Flight"];
export const ALL_PHASES = ["Pre-engagement", "Activation", "In Flight"];

// What the client may see of the roadmap: Thread-only steps (`internal`)
// never; Pre-engagement only on a demo engagement, so a prospect sees the
// whole journey (Alex, 29 Oct 2026). Returns the phases to render and the
// steps with internal ones removed.
export function clientSteps(steps, { demo = false } = {}) {
  const visible = {};
  Object.keys(steps || {}).forEach(phase => {
    if (Array.isArray(steps[phase])) visible[phase] = steps[phase].filter(r => !r.internal);
  });
  return { phases: demo ? ALL_PHASES : CLIENT_PHASES, steps: visible };
}

const todayISO = () => new Date().toLocaleDateString("en-CA");
const monthOf = iso => iso.slice(0, 7);

export function shortDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
}
// "Thu 5 Nov" for lists; "Thursday 5 November" for the Now sentence.
export function weekdayDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" }).replace(",", "");
}
export function longDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" }).replace(",", "");
}

// Where the engagement is. Pre-engagement is Thread's admin checklist and
// never names the phase for the client.
export function currentPhase(steps, typeLabel, phases = CLIENT_PHASES) {
  const s = steps || {};
  for (const phase of phases) {
    const rows = s[phase];
    if (!Array.isArray(rows) || rows.length === 0) continue;
    const done = rows.filter(r => r.status === "complete").length;
    if (done < rows.length) return { label: phase, done, total: rows.length };
  }
  const stored = Object.keys(s).filter(k => Array.isArray(s[k]) && s[k].length > 0);
  if (stored.length === 0) return { label: typeLabel || "Engagement" };
  const allDone = stored.every(k => s[k].every(r => r.status === "complete"));
  if (allDone) return { label: "Complete" };
  // Only Pre-engagement is open
  return { label: "Getting started" };
}

// Every dated thing ahead of today: open roadmap steps, scheduled block
// sessions, coaching rows. Coaching rows are read by date, not by their
// `happened` flag (stored rows default to happened: true; see spec call 1).
function datedAhead({ steps, cfg, finance, blockSessions, today, phases = CLIENT_PHASES }) {
  const t = today || todayISO();
  const out = [];
  phases.forEach(phase => (steps?.[phase] || []).forEach(r => {
    if (r.status !== "complete" && r.date && r.date >= t) out.push({ label: r.label, date: r.date, kind: "step" });
  }));
  (blockSessions || []).forEach(s => {
    if (s.status === "scheduled" && s.date && s.date >= t) out.push({ label: "Coaching session", date: s.date, kind: "session" });
  });
  const months = [monthOf(t), addMonths(monthOf(t), 1)];
  months.forEach(m => ((finance || {})[m]?.coaching || []).forEach(c => {
    if (c.date && c.date >= t) out.push({ label: `Coaching · ${c.label}`, date: c.date, kind: "coaching" });
  }));
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

export function nextUp(args) {
  const ahead = datedAhead(args);
  if (ahead.length > 0) return { label: ahead[0].label, date: ahead[0].date };
  for (const phase of args.phases || CLIENT_PHASES) {
    const open = (args.steps?.[phase] || []).find(r => r.status !== "complete");
    if (open) return { label: open.label, date: null };
  }
  return null;
}

// The one number that is not money: a block's delivered count. Fund balances
// and retainer amounts were dropped from the band (Alex, 28 Oct 2026): they
// are tracked month by month in the statement, not as a headline.
export function headlineNumber(cfg, { blockSessions }) {
  const m = cfg?.modules || {};
  if (!m.sessionBlock) return null;
  const b = calcSessionBlock(m.sessionBlock, blockSessions || []);
  return { label: "Sessions", value: `${b.delivered} of ${b.purchased}`, sub: "delivered", tone: "neutral" };
}

// The roadmap, folded: the last thing done, then the next three. Open steps
// with a date come first (soonest first), then undated ones in stored order.
export function roadmapSlice(steps, n = 3, phases = CLIENT_PHASES) {
  const rows = [];
  phases.forEach(phase => (steps?.[phase] || []).forEach(r => rows.push({ ...r, phase })));
  const done = rows.filter(r => r.status === "complete");
  const dated = done.filter(r => r.date).sort((a, b) => b.date.localeCompare(a.date));
  const last = dated[0] || done[done.length - 1] || null;
  const open = rows.filter(r => r.status !== "complete");
  const next = [...open.filter(r => r.date).sort((a, b) => a.date.localeCompare(b.date)), ...open.filter(r => !r.date)].slice(0, n);
  return { last, next, total: rows.length, hidden: rows.length - (last ? 1 : 0) - next.length };
}

// How a coaching row reads on the client page (spec call 1).
export function coachingState(row, month, today) {
  const t = today || todayISO();
  if (row.date && row.date >= t) return { label: `Scheduled ${shortDate(row.date)}`, tone: "neutral" };
  // Undated and still at the default (happened: true) in a month that is not over: nothing to report yet.
  if (!row.date && row.happened && month >= monthOf(t)) return { label: "This month", tone: "neutral" };
  return row.happened ? { label: "Happened", tone: "good" } : { label: "Didn't happen", tone: "attention" };
}

// One line a client can read for a closed month, plus the fund movements.
export function monthSummary(cfg, finance, month, today) {
  const mc = calcMonth(cfg, finance, month);
  const t = today || todayISO();
  const rows = mc.data.coaching;
  const happened = rows.filter(c => c.happened && !(c.date && c.date >= t)).length;
  const missed = rows.filter(c => !c.happened).length;
  const scheduled = rows.filter(c => c.date && c.date >= t).length;
  const parts = [];
  if (happened) parts.push(`${happened} session${happened === 1 ? "" : "s"}`);
  if (missed) parts.push(`${missed} missed`);
  if (scheduled) parts.push(`${scheduled} scheduled`);
  // Counts only: item labels belong inside the open month, not on its head.
  const fund = mc.data.lineItems.filter(li => !li.billedSeparately).length;
  const sep = mc.data.lineItems.filter(li => li.billedSeparately).length;
  const pool = !!cfg.modules.drawdownPool;
  if (fund) parts.push(pool ? `${fund} from your fund` : `${fund} extra item${fund === 1 ? "" : "s"}`);
  if (sep) parts.push(`${sep} invoiced separately`);
  const seats = cfg.modules.recurringSessions?.mode === "seats";
  if (seats && parts.length === 0) parts.push("Coaching included");
  return {
    line: parts.join(" · "),
    added: Math.round((mc.drawdown + mc.coachingRollover) * 100) / 100,
    spent: mc.lineItemsTotal,
    separate: mc.separateTotal,
    balance: cfg.modules.drawdownPool ? calcCumulativeBalance(cfg, finance, month) : null,
    rollover: mc.coachingRollover,
  };
}
